import { Config } from 'config/envConfig';
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCaretLeft,
    faCaretRight,
    faComment,
    faFile,
    faGlobe,
    faMagnifyingGlassMinus,
    faMagnifyingGlassPlus,
} from '@fortawesome/free-solid-svg-icons';
import { PdfViewer, PdfActions } from './pdfDisplay';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement } from 'react-reflex';
import {
    Button,
    Pagination,
    ImageViewer,
    MarkdownViewer,
    TxtViewer,
} from '@quyva/shared-ui';
import {
    Mode,
    FILE_EXTENSIONS,
    getFileFormat,
    getFileIcon,
    getFileIconColor,
    isImageFile,
    isMarkdownFile,
    isTxtFile,
} from '@quyva/common';
import { getUrl } from '../utils';
import FormRenderer from './formInput/FormRenderer';

const scaleSteps = 0.1;
const minScale = 0.5;
const maxScale = 3;

const FileType = {
    PDF: 'pdf',
    IMAGE: 'image',
    MARKDOWN: 'markdown',
    TEXT: 'text',
};

const viewerComponents = {
    [FileType.PDF]: PdfViewer,
    [FileType.IMAGE]: ImageViewer,
    [FileType.MARKDOWN]: MarkdownViewer,
    [FileType.TEXT]: TxtViewer,
};

const getFileType = (ext) => {
    if ([FILE_EXTENSIONS.PDF, FILE_EXTENSIONS.DOCX].includes(ext)) {
        return FileType.PDF;
    }
    if (isImageFile(ext)) {
        return FileType.IMAGE;
    }
    if (isMarkdownFile(ext)) {
        return FileType.MARKDOWN;
    }
    if (isTxtFile(ext)) {
        return FileType.TEXT;
    }
    return null;
};

const FileViewerWrapper = ({
    extension,
    fileName,
    fileUrl,
    activePageContent,
    activePageNumber,
    activeChunkData,
    setActivePageNumber,
    setTotalPageNumbers,
    scale,
    activeStartLineNumber,
}) => {
    const fileType = getFileType(extension);
    const ViewerComponent = viewerComponents[fileType];

    if (!ViewerComponent) return null;

    const commonProps = {
        fileName,
        fileUrl,
    };

    const typeSpecificProps = {
        pdf: {
            pdfFile: fileUrl,
            highlightTexts: activePageContent,
            pageNumber: activePageNumber,
            activeChunkPage: activeChunkData?.pageNumber || 0,
            setPageNumber: setActivePageNumber,
            setTotalPageNumbers,
            scale,
        },
        markdown: {
            scrollToLine: activeStartLineNumber,
        },
        text: {
            scrollToLine: activeStartLineNumber,
        },
    };

    return (
        <ViewerComponent
            {...commonProps}
            {...(typeSpecificProps[fileType] || {})}
        />
    );
};

export const ViewerContainer = ({
    activeQuestion,
    toggleVisibility,
    isSmallScreen,
    formInPanel,
    onFormSubmit,
    onFormCancel,
}) => {
    const [extension, setExtension] = useState(null);
    const [activeChunkData, setActiveChunkData] = useState(null);
    const [chunksData, setChunksData] = useState([]);
    const [fileUrl, setFileUrl] = useState('');
    const [fileName, setFileName] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [activePageNumber, setActivePageNumber] = useState(0);
    const [activeStartLineNumber, setActiveStartLineNumber] = useState(0);
    const [activeEndLineNumber, setActiveEndLineNumber] = useState(0);
    const [activePageContent, setActivePageContent] = useState(null);
    const [totalPageNumbers, setTotalPageNumbers] = useState(0);
    const [scale, setScale] = useState(window.innerWidth < 1024 ? 0.6 : 1);

    useEffect(() => {
        if (
            activeQuestion &&
            activeQuestion.chunksData &&
            activeQuestion.chunksData.length > 0
        ) {
            const { chunksData } = activeQuestion;

            // Sorting the chunks according to page number.
            // chunksData.sort(function (chunk, anotherChunk) {
            //     return chunk.pageNumber - anotherChunk.pageNumber;
            // });

            // Filtering chunks that has documents to show
            const filteredChunks = chunksData.map((chunk) => {
                if (chunk.fileName) return chunk;
            });

            let firstChunkData = null;
            if (filteredChunks.length) {
                firstChunkData = filteredChunks[0];
            }

            setActiveChunkData(firstChunkData);
            setChunksData(filteredChunks);
        }
    }, [activeQuestion]);

    useEffect(() => {
        if (activeChunkData) {
            const { email, folderName, fileName } = activeChunkData;

            const pageNumber = activeChunkData.pageNumber;
            let startLineNumber = null;
            let endLineNumber = null;
            if ('startLineNumber' in activeChunkData) {
                startLineNumber = activeChunkData.startLineNumber;
            }
            if ('endLineNumber' in activeChunkData) {
                endLineNumber = activeChunkData.endLineNumber;
            }
            const pageContent = { chunks: [activeChunkData.pageContent] };
            const actualFileName = activeChunkData.actualFileName;
            const fileExtension = getFileFormat(actualFileName ?? fileName);
            let fileUrl = '';

            if (isImageFile(fileExtension)) {
                const path = `${email}/${folderName}/${fileName}`;
                const encodedPath = encodeURIComponent(path);
                fileUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_image/?file_path=${encodedPath}`
                );
            } else if (isMarkdownFile(fileExtension)) {
                fileUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_markdown/?file_name=${fileName}&email=${email}&folder_name=${folderName}`
                );
            } else if (isTxtFile(fileExtension)) {
                fileUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_txt_file/?file_name=${fileName}&email=${email}&folder_name=${folderName}`
                );
            } else {
                const pdfFileName = fileName?.replace(fileExtension, 'pdf');
                const path = `${email}/${folderName}/${pdfFileName}`;
                const encodedPath = encodeURIComponent(path);
                fileUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_pdf/?file_path=${encodedPath}&mode=${Mode.CHAT}`
                );
            }

            if (fileExtension) {
                setExtension(fileExtension.toLowerCase());
            }

            setFileUrl(fileUrl);
            if (startLineNumber != null) {
                setActiveStartLineNumber(startLineNumber);
            }
            if (endLineNumber != null) {
                setActiveEndLineNumber(endLineNumber);
            }
            setFileName(actualFileName ?? fileName);
            setActivePageNumber(pageNumber);
            setActivePageContent(pageContent);
        }
    }, [activeChunkData]);

    const handleSourceChange = (index) => {
        const newActiveChunk = chunksData[index];
        const pageNumber = newActiveChunk.pageNumber;
        if ('startLineNumber' in newActiveChunk) {
            const startLineNumber = newActiveChunk.startLineNumber;
            setActiveStartLineNumber(startLineNumber);
        }
        if ('endLineNumber' in newActiveChunk) {
            const endLineNumber = newActiveChunk.endLineNumber;
            setActiveEndLineNumber(endLineNumber);
        }
        setActiveIndex(index);
        setActiveChunkData(newActiveChunk);
        setActivePageNumber(pageNumber);
    };

    // A detailed-view form takes over the panel instead of the document viewer.
    if (formInPanel) {
        return (
            <div className='w-full h-full p-3 overflow-hidden bg-backgrounds-secondary'>
                <FormRenderer
                    formConfig={formInPanel}
                    onSubmit={onFormSubmit}
                    onCancel={onFormCancel}
                    isPanel
                />
            </div>
        );
    }

    return (
        <ReflexContainer orientation='horizontal'>
            {window.innerWidth <= 1024 && activeQuestion && (
                <div className='fixed bottom-24 right-8 z-20 scale-110'>
                    <Button
                        className={'mx-2 w-30 h-30'}
                        //text={'Chat'}
                        hoverText={'View Chat'}
                        icon={faComment}
                        onClick={(e) => {
                            toggleVisibility();
                        }}
                        type={'success'}
                    />
                </div>
            )}
            <ReflexElement
                className='header border-b'
                style={{
                    minHeight: '3.5rem',
                    height: '3.5rem',
                    maxHeight: '3.5rem',
                }}
            >
                <div className='pane-content w-full h-full p-2 select-none'>
                    <div className='flex h-full items-center justify-between my-0 mx-2'>
                        <div
                            className={`${
                                !activeChunkData?.associated_website && (
                                    extension === FILE_EXTENSIONS.PDF ||
                                    extension === FILE_EXTENSIONS.DOCX ||
                                    isTxtFile(extension) ||
                                    isMarkdownFile(extension)
                                ) ? 'w-1/2' : 'w-full'
                            } my-1 text-texts-secondary300 truncate`}
                        >
                            {activeChunkData?.associated_website ? (
                                <div className='flex items-center truncate'>
                                    <FontAwesomeIcon
                                        icon={faGlobe}
                                        className='mr-2 drop-shadow text-blue-500'
                                    />
                                    <div className='truncate font-semibold text-base'>
                                        {activeChunkData.associated_website}
                                    </div>
                                </div>
                            ) : fileName ? (
                                <div className='flex items-center truncate'>
                                    <FontAwesomeIcon
                                        icon={getFileIcon(fileName)}
                                        color={getFileIconColor(fileName)}
                                        className='mr-2 drop-shadow'
                                    />
                                    <div className='truncate font-semibold text-base'>
                                        {fileName}
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>

                        {(extension === FILE_EXTENSIONS.PDF ||
                            extension === FILE_EXTENSIONS.DOCX) && (
                            <div className='w-1/2'>
                                <PdfActions
                                    pageNumber={activePageNumber}
                                    totalPageNumbers={totalPageNumbers}
                                    setPageNumber={setActivePageNumber}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ReflexElement>

            <ReflexElement flex={1}>
                <div className='pane-content w-full h-full'>
                    {activeChunkData?.associated_website ? (
                        <div className='w-full h-full flex items-center justify-center bg-blue-50 p-6'>
                            <div className='relative bg-white rounded-xl shadow-sm border border-blue-100 px-6 py-8 w-full max-w-xs flex flex-col items-center gap-3'
                                 style={{ clipPath: 'polygon(0 0, calc(100% - 28px) 0, 100% 28px, 100% 100%, 0 100%)' }}>
                                {/* Folded corner */}
                                <div className='absolute top-0 right-0' style={{
                                    width: 0, height: 0,
                                    borderStyle: 'solid',
                                    borderWidth: '28px 28px 0 0',
                                    borderColor: '#bfdbfe transparent transparent transparent',
                                }} />
                                <FontAwesomeIcon icon={faGlobe} className='text-blue-400 text-2xl' />
                                <p className='text-gray-500 text-sm text-center'>
                                    Content sourced from
                                </p>
                                <a
                                    href={activeChunkData.associated_website}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-blue-600 underline text-sm text-center break-all hover:text-blue-800'
                                >
                                    {activeChunkData.associated_website}
                                </a>
                                <p className='text-gray-400 text-xs text-center'>
                                    Click the link to visit the original page.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <FileViewerWrapper
                            extension={extension}
                            fileName={fileName}
                            fileUrl={fileUrl}
                            activePageContent={activePageContent}
                            activePageNumber={activePageNumber}
                            activeChunkData={activeChunkData}
                            setActivePageNumber={setActivePageNumber}
                            setTotalPageNumbers={setTotalPageNumbers}
                            scale={scale}
                            activeStartLineNumber={activeStartLineNumber}
                        />
                    )}
                </div>
            </ReflexElement>

            <ReflexElement
                className='footer border-t'
                style={{
                    minHeight: '4rem',
                    height: '4rem',
                    maxHeight: '4rem',
                }}
            >
                <div className='pane-content w-full h-full select-none'>
                    <div className='flex items-center justify-between w-full h-full px-4'>
                        <div className='flex justify-center items-center mr-2'>
                            <div className='flex justify-center items-center text-texts-secondary300 truncate'>
                                <FontAwesomeIcon
                                    icon={faFile}
                                    className='mr-2'
                                />
                                <div className='truncate font-semibold'>
                                    Document Source
                                </div>
                            </div>
                            <Pagination
                                activeId={
                                    activeQuestion ? activeQuestion.qid : null
                                }
                                itemsCount={chunksData.length}
                                itemsPerPage={1}
                                handlePageChange={handleSourceChange}
                                previousLabel={
                                    <Button
                                        hoverText={'Go to previous source'}
                                        icon={faCaretLeft}
                                        type={'warning'}
                                        disabled={activeIndex === 0}
                                    />
                                }
                                nextLabel={
                                    <Button
                                        hoverText={'Go to next source'}
                                        icon={faCaretRight}
                                        type={'warning'}
                                        disabled={
                                            activeIndex ===
                                            chunksData.length - 1
                                        }
                                    />
                                }
                            />
                        </div>

                        {/* Scale actions */}
                        {(extension === FILE_EXTENSIONS.PDF ||
                            extension === FILE_EXTENSIONS.TXT ||
                            extension === FILE_EXTENSIONS.DOCX) && (
                            <div className='flex items-center rounded-full ml-2 border'>
                                <Button
                                    icon={faMagnifyingGlassMinus}
                                    type={'warning'}
                                    onClick={() => {
                                        const newScale = parseFloat(
                                            parseFloat(scale) - scaleSteps
                                        ).toFixed(1);
                                        setScale(newScale);
                                    }}
                                    hoverText={
                                        'Decrease the viewing scale of document'
                                    }
                                    disabled={scale <= minScale}
                                />
                                <div className='w-6 flex justify-center text-sm mx-1 text-texts-secondary300 font-semibold text-nowrap font-semibold'>
                                    {scale}x
                                </div>
                                <Button
                                    icon={faMagnifyingGlassPlus}
                                    type={'warning'}
                                    onClick={() => {
                                        const newScale = parseFloat(
                                            parseFloat(scale) + scaleSteps
                                        ).toFixed(1);
                                        setScale(newScale);
                                    }}
                                    hoverText={
                                        'Increase the viewing scale of document'
                                    }
                                    disabled={scale >= maxScale}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ReflexElement>
        </ReflexContainer>
    );
};
